import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

const CHANNEL_PRIORITY = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Sms': 3,
  'Channel::WebWidget': 5,
  'Channel::Api': 6,
};

export const generateLabelForContactableInboxesList = ({
  name,
  email,
  channelType,
  phoneNumber,
}) => {
  if (channelType === INBOX_TYPES.EMAIL) {
    return `${name} (${email})`;
  }
  if (channelType === INBOX_TYPES.WHATSAPP) {
    return phoneNumber ? `${name} (${phoneNumber})` : name;
  }
  return name;
};

const transformInbox = ({
  name,
  id,
  email,
  channelType,
  phoneNumber,
  medium,
  ...rest
}) => ({
  id,
  icon: getInboxIconByType(channelType, medium, 'line'),
  label: generateLabelForContactableInboxesList({
    name,
    email,
    channelType,
    phoneNumber,
  }),
  action: 'inbox',
  value: id,
  name,
  email,
  phoneNumber,
  channelType,
  medium,
  ...rest,
});

export const compareInboxes = (a, b) => {
  // Channels that have no priority defined should come at the end.
  const priorityA = CHANNEL_PRIORITY[a.channelType] || 999;
  const priorityB = CHANNEL_PRIORITY[b.channelType] || 999;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  const nameA = a.name || '';
  const nameB = b.name || '';
  return nameA.localeCompare(nameB);
};

export const buildContactableInboxesList = contactInboxes => {
  if (!contactInboxes) return [];

  return contactInboxes.map(transformInbox).sort(compareInboxes);
};

export const getCapitalizedNameFromEmail = email => {
  const name = email.match(/^([^@]*)@/)?.[1] || email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const processContactableInboxes = inboxes => {
  return inboxes.map(inbox => ({
    ...inbox.inbox,
    sourceId: inbox.sourceId,
  }));
};

export const mergeInboxDetails = (inboxesData, inboxesList = []) => {
  if (!inboxesData || !inboxesData.length) {
    return [];
  }

  // Contact inboxes come from the contact endpoint and can outlive an inbox
  // being archived or disconnected. Only expose an inbox when it is still in
  // the operational inbox list received by the client.
  return inboxesData.flatMap(inboxData => {
    const matchingInbox = inboxesList.find(
      inbox => Number(inbox.id) === Number(inboxData.id)
    );
    if (!matchingInbox) return [];

    return {
      ...camelcaseKeys(matchingInbox, { deep: true }),
      ...inboxData,
    };
  });
};

export const prepareAttachmentPayload = (
  attachedFiles,
  directUploadsEnabled
) => {
  const files = [];
  attachedFiles.forEach(attachment => {
    if (attachment.blobSignedId) {
      files.push(attachment.blobSignedId);
    } else if (directUploadsEnabled) {
      files.push(attachment.blobSignedId);
    } else {
      // Fallback: If it's a vue-upload-component file, it has .file. Otherwise it's a native File.
      files.push(attachment.resource.file || attachment.resource);
    }
  });
  return files;
};

export const prepareNewMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  subject,
  ccEmails,
  bccEmails,
  currentUser,
  attachedFiles = [],
  directUploadsEnabled = false,
  activeMessageTemplate = null,
  directSend = false,
}) => {
  const payload = {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: Number(selectedContact.id),
    message: { content: message },
    assigneeId: currentUser.id,
  };

  if (directSend) {
    payload.message.content_attributes = { direct_send: true };
  }

  if (activeMessageTemplate?.variables) {
    const vars = activeMessageTemplate.variables;
    // preserve template_params so we don't lose the image/footer
    const baseAttributes = { template_params: activeMessageTemplate.variables };

    if (vars.format === 'list') {
      payload.message.content_type = 'input_select';
      payload.message.content_attributes = {
        ...baseAttributes,
        items: vars.listItems || [],
        button_text: vars.listButtonText || 'Opções',
      };
    } else if (
      ['buttons', 'quick_replies', 'personalized'].includes(vars.format)
    ) {
      payload.message.content_type = 'cards';
      payload.message.content_attributes = {
        ...baseAttributes,
        items: [
          {
            title: '',
            actions: (activeMessageTemplate.buttons || vars.buttons || []).map(
              b => {
                const type =
                  { url: 'link', phone: 'phone', reply: 'postback' }[b.type] ||
                  'postback';

                return {
                  type,
                  text: b.label || 'Botão',
                  payload: b.payload || b.url || '',
                  uri: b.url || '',
                };
              }
            ),
          },
        ],
      };
    } else if (vars.format === 'carousel') {
      payload.message.content_type = 'cards';
      payload.message.content_attributes = {
        ...baseAttributes,
        items: (vars.carouselItems || []).map(item => ({
          title: item.title,
          description: item.description,
          media_url: item.imageUrl,
          actions: [
            {
              type: 'link',
              text: item.url ? 'Acessar Link' : 'Ação',
              uri: item.url || '',
              payload: item.url || '',
            },
          ],
        })),
      };
    } else {
      payload.message.content_attributes = baseAttributes;
    }

    if (targetInbox.channelType === INBOX_TYPES.WHATSAPP) {
      const contactName = selectedContact.name || '';

      payload.message.template_params = {
        is_custom: true,
        custom_template_id: activeMessageTemplate.id,
        language: vars.language || 'pt_BR',
        active_message_variables: {
          ...vars,
          nome: vars.nome || contactName,
          name: vars.name || contactName,
        },
        active_message_media_url: vars.headerUrl || null,
      };
    }
  }

  if (attachedFiles?.length) {
    payload.files = prepareAttachmentPayload(
      attachedFiles,
      directUploadsEnabled
    );
  }

  if (subject) {
    payload.mailSubject = subject;
  }

  if (ccEmails) {
    payload.message.cc_emails = ccEmails;
  }

  if (bccEmails) {
    payload.message.bcc_emails = bccEmails;
  }

  return payload;
};

export const prepareWhatsAppMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  contentType,
  contentAttributes,
  templateParams,
  currentUser,
}) => {
  const payload = {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: selectedContact.id,
    message: { content: message, template_params: templateParams },
    assigneeId: currentUser.id,
  };

  if (contentType) payload.message.content_type = contentType;
  if (contentAttributes) payload.message.content_attributes = contentAttributes;

  return payload;
};

// API Calls
const MIN_SEARCH_LENGTH = 2;

export const createContactSearcher = () => {
  let controller = null;

  return async (
    query,
    { skipMinLength = false, reachableOnly = true } = {}
  ) => {
    const trimmed = typeof query === 'string' ? query.trim() : '';

    controller?.abort();

    if (!trimmed || (!skipMinLength && trimmed.length < MIN_SEARCH_LENGTH))
      return [];

    controller = new AbortController();
    const { signal } = controller;

    try {
      const {
        data: { payload },
      } = await ContactAPI.search(trimmed, 1, 'name', '', { signal });

      const camelCasedPayload = camelcaseKeys(payload, { deep: true });
      if (!reachableOnly) return camelCasedPayload || [];

      // Filter contacts that have either phone_number or email
      const filteredPayload = camelCasedPayload?.filter(
        contact => contact.phoneNumber || contact.email
      );
      return filteredPayload || [];
    } catch (error) {
      // Return null for aborted requests so callers can distinguish
      // "request was cancelled" from "no results found"
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return null;
      }
      throw error;
    }
  };
};

export const createNewContact = async input => {
  const payload = {
    name: input.startsWith('+')
      ? input.slice(1) // Remove the '+' prefix if it exists
      : getCapitalizedNameFromEmail(input),
    ...(input.startsWith('+') ? { phone_number: input } : { email: input }),
  };

  const {
    data: {
      payload: { contact: newContact },
    },
  } = await ContactAPI.create(payload);

  return camelcaseKeys(newContact, { deep: true });
};

export const fetchContactableInboxes = async contactId => {
  const {
    data: { payload: inboxes = [] },
  } = await ContactAPI.getContactableInboxes(contactId);

  const convertInboxesToCamelKeys = camelcaseKeys(inboxes, { deep: true });

  return processContactableInboxes(convertInboxesToCamelKeys);
};
